import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { toast } from 'sonner'
import {
  getNotifications,
  getNotification,
  getNotificationDeliveries,
  createNotification,
  previewNotificationAudience,
  sendNotification,
  type NotificationAudience,
  type NotificationPreview,
} from '@/services'
import { AppButton, EmptyState, FilterChip, Skeleton, StatusBadge, Modal, TableSkeleton, rowActivation } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'
import { relTime, formatDateTime } from '@/lib/utils'

const AUDIENCE_LABEL: Record<NotificationAudience, string> = {
  single_tenant: 'Un tenant',
  all_tenants: 'Todos',
  plan_ids: 'Por plan',
  status: 'Por estado',
}

function NotificationDetailSkeleton() {
  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <Skeleton width={160} height={22} style={{ marginBottom: 10 }} />
          <Skeleton width={300} height={32} />
          <Skeleton width={260} height={14} style={{ marginTop: 10 }} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card"><div className="card-header"><h3>Cuerpo</h3></div><div className="card-body"><Skeleton height={220} /></div></div>
        <div className="card"><div className="card-header"><h3>Métricas</h3></div><div className="card-body"><Skeleton height={160} /></div></div>
      </div>
    </>
  )
}

// ---------- LIST ----------
export function NotificationsPage() {
  const navigate = useNavigate()
  const [filter, setFilter] = useState('all')
  const { data: result, isLoading } = useQuery({
    queryKey: ['notifications', filter],
    queryFn: () => getNotifications({ status: filter === 'all' ? undefined : (filter as any), pageSize: 100 }),
  })
  const items = result?.data || []
  const total = result?.count || items.length

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Clientes</div>
          <h1>Notificaciones</h1>
          <div className="subtitle">Comunicaciones por email a tenants. Por ahora solo email — próximamente WhatsApp e in-app.</div>
        </div>
        <div className="page-actions">
          <AppButton variant="primary" icon="send" onClick={() => navigate('/notifications/new')}>Nuevo mensaje</AppButton>
        </div>
      </div>

      <div className="table-wrap">
        <div className="table-toolbar">
          <FilterChip active={filter === 'all'} onClick={() => setFilter('all')} label="Todas" count={isLoading ? undefined : total} />
          <FilterChip active={filter === 'sent'} onClick={() => setFilter('sent')} label="Enviadas" count={isLoading ? undefined : items.filter((i) => i.status === 'sent').length} />
          <FilterChip active={filter === 'draft'} onClick={() => setFilter('draft')} label="Borradores" count={isLoading ? undefined : items.filter((i) => i.status === 'draft').length} />
        </div>
        {isLoading ? (
          <TableSkeleton columns={8} rows={8} />
        ) : items.length === 0 ? (
          <EmptyState icon="send" title="Todavía no enviaste mensajes" desc="Generá un mensaje nuevo desde el botón de arriba." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Subject</th><th>Audiencia</th><th>Canal</th><th>Status</th>
                <th style={{ textAlign: 'right' }}>Destinatarios</th>
                <th style={{ textAlign: 'right' }}>Entregados</th>
                <th style={{ textAlign: 'right' }}>Fallaron</th>
                <th>Cuándo</th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} onClick={() => navigate(`/notifications/${n.id}`)} {...rowActivation(() => navigate(`/notifications/${n.id}`))}>
                  <td><span className="cell-primary">{n.subject}</span></td>
                  <td><span className="badge badge-outline badge-sm">{AUDIENCE_LABEL[n.audience] || n.audience}</span></td>
                  <td><span className="row gap-1"><Icon name="send" size={11} />Email</span></td>
                  <td><StatusBadge status={n.status} size="sm" /></td>
                  <td style={{ textAlign: 'right' }}><span className="tabular">{n.stats?.recipients ?? 0}</span></td>
                  <td style={{ textAlign: 'right' }}><span className="tabular" style={{ color: (n.stats?.sent || 0) > 0 ? 'var(--success-fg)' : 'var(--text-3)' }}>{n.stats?.sent ?? 0}</span></td>
                  <td style={{ textAlign: 'right' }}><span className="tabular" style={{ color: (n.stats?.failed || 0) > 0 ? 'var(--danger-fg)' : 'var(--text-3)' }}>{n.stats?.failed ?? 0}</span></td>
                  <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{n.sent_at ? relTime(n.sent_at) : '—'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

// ---------- COMPOSER ----------
export function NotificationComposerPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [audience, setAudience] = useState<NotificationAudience>('all_tenants')
  const [preview, setPreview] = useState<NotificationPreview | null>(null)
  const [showConfirm, setShowConfirm] = useState(false)

  const previewMutation = useMutation({
    mutationFn: () => previewNotificationAudience({ audience }),
    onSuccess: (data) => setPreview(data),
    onError: (err: any) => toast.error(err?.message || 'No se pudo calcular la audiencia'),
  })

  const sendMutation = useMutation({
    mutationFn: async () => {
      const created = await createNotification({ subject, body, audience })
      return sendNotification(created.id)
    },
    onSuccess: () => {
      toast.success('Mensaje enviado')
      qc.invalidateQueries({ queryKey: ['notifications'] })
      setShowConfirm(false)
      navigate('/notifications')
    },
    onError: (err: any) => { toast.error(err?.message || 'No se pudo enviar'); setShowConfirm(false) },
  })

  const audienceOptions: Array<{ id: NotificationAudience; label: string; desc: string }> = [
    { id: 'single_tenant', label: 'Un tenant específico', desc: 'Para mandar a un solo cliente puntual.' },
    { id: 'all_tenants', label: 'Todos los tenants', desc: 'Tenants activos, en trial y past_due (no cancelados).' },
    { id: 'plan_ids', label: 'Por plan', desc: 'Los planes que querés alcanzar.' },
    { id: 'status', label: 'Por estado de subscription', desc: 'Útil para campañas a past_due o trials.' },
  ]

  return (
    <>
      <div className="page-header" style={{ marginBottom: 18 }}>
        <div className="page-title-block">
          <div className="row gap-2" style={{ marginBottom: 6, alignItems: 'center' }}>
            <Link to="/notifications" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>← Notificaciones</Link>
            <span className="badge badge-outline badge-sm">Borrador</span>
          </div>
          <h1>Nuevo mensaje</h1>
          <div className="subtitle">Componé el mail, definí la audiencia y calculá destinatarios antes de enviar.</div>
        </div>
        <div className="page-actions">
          <AppButton variant="success" icon="send" disabled={!subject || !body || !preview || (preview.with_email === 0)} onClick={() => setShowConfirm(true)}>
            {preview ? `Enviar a ${preview.with_email}` : 'Enviar'}
          </AppButton>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 16 }}>
        <div className="col gap-4">
          <div className="card">
            <div className="card-header"><h3>Mensaje</h3></div>
            <div className="card-body">
              <div className="form-row">
                <label htmlFor="notif-subject">Asunto</label>
                <input id="notif-subject" className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Asunto del email" />
              </div>
              <div className="form-row">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <label htmlFor="notif-body">Cuerpo</label>
                  <span className="muted" style={{ fontSize: 11.5 }}>Texto plano · WYSIWYG llega en V2</span>
                </div>
                <textarea id="notif-body" className="textarea" value={body} onChange={(e) => setBody(e.target.value)} style={{ minHeight: 240, fontFamily: 'var(--font-body)', lineHeight: 1.6 }} />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h3>Audiencia</h3></div>
            <div className="card-body">
              <div style={{ display: 'grid', gap: 8 }}>
                {audienceOptions.map((opt) => (
                  <label key={opt.id} style={{ display: 'flex', gap: 12, padding: 12, border: '1px solid ' + (audience === opt.id ? 'var(--bisne-green)' : 'var(--border)'), background: audience === opt.id ? 'var(--success-bg)' : 'var(--surface)', borderRadius: 8, cursor: 'pointer', alignItems: 'flex-start' }}>
                    <input type="radio" name="audKind" checked={audience === opt.id} onChange={() => { setAudience(opt.id); setPreview(null) }} style={{ marginTop: 2 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5 }}>{opt.label}</div>
                      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col gap-4" style={{ position: 'sticky', top: 16, alignSelf: 'flex-start' }}>
          <div className="card">
            <div className="card-header"><h3>Preview del email</h3></div>
            <div style={{ padding: 16, background: 'var(--bg-2)' }}>
              <div style={{ background: 'white', padding: 20, borderRadius: 8, border: '1px solid var(--border)', maxHeight: 360, overflow: 'auto' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 20, letterSpacing: '-0.04em', marginBottom: 18 }}>
                  bisne<span style={{ width: 6, height: 6, background: 'var(--bisne-green)', borderRadius: '50%' }} />
                </div>
                <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 20, letterSpacing: '-0.01em', marginBottom: 12 }}>{subject || <span className="muted">(sin asunto)</span>}</div>
                <pre style={{ margin: 0, fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: 1.6, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{body || <span className="muted">(sin cuerpo)</span>}</pre>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3>Destinatarios</h3>
              <AppButton variant="success" size="sm" icon="refresh" onClick={() => previewMutation.mutate()}>Calcular</AppButton>
            </div>
            <div className="card-body">
              {!preview ? (
                <div className="muted" style={{ fontSize: 13, textAlign: 'center', padding: 16 }}>Hacé click en "Calcular" para ver a quién va el mensaje.</div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
                    <div style={{ padding: 12, borderRight: '1px solid var(--border)', textAlign: 'center' }}>
                      <div className="stat-label">Total</div><div className="stat-value">{preview.recipients}</div>
                    </div>
                    <div style={{ padding: 12, borderRight: '1px solid var(--border)', textAlign: 'center', background: 'var(--success-bg)' }}>
                      <div className="stat-label" style={{ color: 'var(--success-fg)' }}>Con email</div><div className="stat-value" style={{ color: 'var(--success-fg)' }}>{preview.with_email}</div>
                    </div>
                    <div style={{ padding: 12, textAlign: 'center', background: 'var(--warning-bg)' }}>
                      <div className="stat-label" style={{ color: 'var(--warning-fg)' }}>Sin email</div><div className="stat-value" style={{ color: 'var(--warning-fg)' }}>{preview.without_email}</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        title="Confirmar envío"
        subtitle="Una vez enviado no se puede deshacer."
        footer={
          <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
            <AppButton variant="ghost" onClick={() => setShowConfirm(false)}>Cancelar</AppButton>
            <AppButton variant="success" icon="send" disabled={sendMutation.isPending} onClick={() => sendMutation.mutate()}>{sendMutation.isPending ? 'Enviando…' : 'Sí, enviar'}</AppButton>
          </div>
        }
      >
        <p style={{ fontSize: 14, lineHeight: 1.5 }}>
          Vas a mandar a <strong>{preview?.with_email || 0} destinatarios</strong>
          {preview && preview.without_email > 0 ? <span className="muted"> ({preview.without_email} sin email serán "skipped")</span> : null}. ¿Confirmás?
        </p>
      </Modal>
    </>
  )
}

// ---------- DETAIL ----------
export function NotificationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [deliveryFilter, setDeliveryFilter] = useState('all')

  const { data: n } = useQuery({ queryKey: ['notification', id], queryFn: () => getNotification(id!), enabled: !!id })
  const { data: deliveriesResult } = useQuery({
    queryKey: ['notification-deliveries', id, deliveryFilter],
    queryFn: () => getNotificationDeliveries(id!, { status: deliveryFilter === 'all' ? undefined : (deliveryFilter as any), pageSize: 200 }),
    enabled: !!id && n?.status === 'sent',
  })

  const deliveries = deliveriesResult?.data || []

  if (!n) {
    return <NotificationDetailSkeleton />
  }

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="row gap-2" style={{ marginBottom: 6 }}>
            <Link to="/notifications" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>← Notificaciones</Link>
            <StatusBadge status={n.status} size="sm" />
          </div>
          <h1>{n.subject}</h1>
          <div className="subtitle">{AUDIENCE_LABEL[n.audience] || n.audience} · {n.sent_at ? formatDateTime(n.sent_at) : 'sin enviar'}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-header"><h3>Cuerpo</h3></div>
          <div className="card-body">
            <pre style={{ margin: 0, fontFamily: 'var(--font-body)', fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{n.body || '—'}</pre>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Métricas</h3></div>
          <div className="card-body">
            <dl className="dl">
              <dt>Destinatarios</dt><dd>{n.stats?.recipients ?? 0}</dd>
              <dt>Entregados</dt><dd>{n.stats?.sent ?? 0}</dd>
              <dt>Fallaron</dt><dd>{n.stats?.failed ?? 0}</dd>
              <dt>Skipped</dt><dd>{n.stats?.skipped ?? 0}</dd>
            </dl>
          </div>
        </div>
      </div>

      {n.status === 'sent' ? (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header">
            <h3>Entregas</h3>
            <div className="row gap-2">
              {['all', 'sent', 'failed', 'skipped'].map((s) => (
                <FilterChip key={s} active={deliveryFilter === s} onClick={() => setDeliveryFilter(s)} label={s === 'all' ? 'Todas' : s} />
              ))}
            </div>
          </div>
          <div className="card-body flush">
            {deliveries.length === 0 ? (
              <EmptyState icon="send" title="Sin entregas" desc="No hay entregas con ese filtro." />
            ) : (
              <table className="table">
                <thead><tr><th>Email</th><th>Status</th><th>Error</th><th>Cuándo</th></tr></thead>
                <tbody>
                  {deliveries.map((d) => (
                    <tr key={d.id}>
                      <td>{d.email || '—'}</td>
                      <td><StatusBadge status={d.status === 'sent' ? 'success' : d.status === 'failed' ? 'error' : 'cancelled'} size="sm" /></td>
                      <td><span style={{ fontSize: 11, color: 'var(--danger-fg)' }}>{d.error || ''}</span></td>
                      <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{relTime(d.created_at)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}
    </>
  )
}
